import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListSubheader,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { projectsAPI, tasksAPI, timeEntriesAPI, customersAPI } from '../services/api';
import { localISODate } from '../utils/dates';
import { recentCustomerIds as recentIds, sortCustomersByRecent, splitTasks, latestOnly } from '../utils/timeEntryTarget';
import { useAuth } from '../contexts/AuthContext';

const TimeTrackingPage = () => {
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]); // Store all projects for filtering
  const [standardTasks, setStandardTasks] = useState([]);
  const [timetrackerTasks, setTimetrackerTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Inline editing
  const [editingEntry, setEditingEntry] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Full-edit dialog
  const [editDialogEntry, setEditDialogEntry] = useState(null);
  const [editDialogData, setEditDialogData] = useState({
    customer_id: '',
    project_id: '',
    task_id: '',
    subtask_comment: '',
    hours: '',
    date: '',
  });
  const [editDialogProjects, setEditDialogProjects] = useState([]);
  const [editDialogStandardTasks, setEditDialogStandardTasks] = useState([]);
  const [editDialogTimetrackerTasks, setEditDialogTimetrackerTasks] = useState([]);
  const [editDialogSaving, setEditDialogSaving] = useState(false);

  // Expanded projects
  const [expandedProjects, setExpandedProjects] = useState({});

  const [formData, setFormData] = useState({
    customer_id: '',
    project_id: '',
    task_id: '',
    subtask_comment: '',
    hours: '',
    date: localISODate(),
  });

  const [editingId, setEditingId] = useState(null);

  const { user } = useAuth();
  // Nur die jüngste Task-Liste übernehmen (schneller Kunden-/Projektwechsel)
  const latestTasks = useRef(latestOnly());
  const latestDialogTasks = useRef(latestOnly());

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter projects when customer changes
  useEffect(() => {
    if (formData.customer_id) {
      const filtered = allProjects.filter(p => p.customer_id === parseInt(formData.customer_id));
      setProjects(filtered);
      // Reset project and task if current project doesn't belong to selected customer
      const currentProject = allProjects.find(p => p.id === parseInt(formData.project_id));
      if (!currentProject || currentProject.customer_id !== parseInt(formData.customer_id)) {
        setFormData(prev => ({ ...prev, project_id: '', task_id: '' }));
      }
    } else {
      setProjects(allProjects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.customer_id, allProjects]);

  // Load tasks when project or customer changes
  useEffect(() => {
    if (formData.project_id) {
      fetchTasks({ project_id: formData.project_id });
    } else if (formData.customer_id) {
      // Load tasks directly linked to customer (no project)
      fetchTasks({ customer_id: formData.customer_id });
    } else {
      setStandardTasks([]);
      setTimetrackerTasks([]);
      setFormData(prev => ({ ...prev, task_id: '' }));
    }
  }, [formData.project_id, formData.customer_id]);

  // Filter projects in filter section when filter customer changes
  useEffect(() => {
    if (filterCustomer) {
      const filtered = allProjects.filter(p => p.customer_id === parseInt(filterCustomer));
      setProjects(filtered);
      // Reset project filter if current project doesn't belong to selected customer
      const currentProject = allProjects.find(p => p.id === parseInt(filterProject));
      if (!currentProject || currentProject.customer_id !== parseInt(filterCustomer)) {
        setFilterProject('');
      }
    } else {
      setProjects(allProjects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCustomer, allProjects]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [customersRes, projectsRes, entriesRes] = await Promise.all([
        customersAPI.getAll(),
        projectsAPI.getAll(),
        timeEntriesAPI.getAll({ user_id: user.id }),
      ]);
      setCustomers(customersRes.data);
      setAllProjects(projectsRes.data);
      setProjects(projectsRes.data);
      setTimeEntries(entriesRes.data);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async (params) => {
    try {
      const response = await latestTasks.current(tasksAPI.getAll(params));
      const { tickets, tracker } = splitTasks(response.data);
      setStandardTasks(tickets);
      setTimetrackerTasks(tracker);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };

  // Group entries by project (or customer if no project)
  const getGroupedEntries = () => {
    let filtered = [...timeEntries];

    // Apply filters
    if (filterCustomer) {
      const customerIdInt = parseInt(filterCustomer);
      filtered = filtered.filter(entry => entry.customer_id === customerIdInt);
    }
    if (filterProject) {
      filtered = filtered.filter(entry => entry.project_id === parseInt(filterProject));
    }
    if (filterDateFrom) {
      filtered = filtered.filter(entry => entry.date >= filterDateFrom);
    }
    if (filterDateTo) {
      filtered = filtered.filter(entry => entry.date <= filterDateTo);
    }

    // Group: Projekt > Kunde > "Ohne Zuordnung"
    const grouped = {};
    filtered.forEach(entry => {
      let key, label;
      if (entry.project_id) {
        key = `p-${entry.project_id}`;
        label = entry.project_name;
      } else if (entry.customer_id) {
        key = `c-${entry.customer_id}`;
        label = entry.customer_name;
      } else {
        key = 'none';
        label = 'Ohne Projekt/Kunde';
      }
      if (!grouped[key]) {
        grouped[key] = {
          group_key: key,
          group_label: label,
          entries: [],
          total_hours: 0,
        };
      }
      grouped[key].entries.push(entry);
      grouped[key].total_hours += parseFloat(entry.hours || 0);
    });

    return Object.values(grouped).sort((a, b) =>
      (a.group_label || '').localeCompare(b.group_label || '')
    );
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Inline editing handlers
  const handleEditClick = (entryId, currentHours) => {
    setEditingEntry(entryId);
    setEditValue(currentHours.toString());
  };

  const handleEditCancel = () => {
    setEditingEntry(null);
    setEditValue('');
  };

  const handleEditSave = async (entryId) => {
    const newHours = parseFloat(editValue);

    if (isNaN(newHours) || newHours < 0) {
      setError('Bitte geben Sie eine gültige Stundenanzahl ein (mindestens 0)');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await timeEntriesAPI.update(entryId, { hours: newHours });

      // Update local state
      setTimeEntries(prevEntries =>
        prevEntries.map(entry =>
          entry.id === entryId ? { ...entry, hours: newHours } : entry
        )
      );

      setSuccess('Stunden erfolgreich aktualisiert');
      setEditingEntry(null);
      setEditValue('');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren der Stunden');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e, entryId) => {
    if (e.key === 'Enter') {
      handleEditSave(entryId);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Full-edit dialog
  const fetchTasksForDialog = async (params) => {
    try {
      const response = await latestDialogTasks.current(tasksAPI.getAll(params));
      const { tickets, tracker } = splitTasks(response.data);
      setEditDialogStandardTasks(tickets);
      setEditDialogTimetrackerTasks(tracker);
    } catch (err) {
      console.error('Failed to load tasks for dialog:', err);
    }
  };

  const openEditDialog = async (entry) => {
    const customerId = entry.customer_id ? String(entry.customer_id) : '';
    const projectId = entry.project_id ? String(entry.project_id) : '';
    setEditDialogEntry(entry);
    setEditDialogData({
      customer_id: customerId,
      project_id: projectId,
      task_id: entry.task_id ? String(entry.task_id) : '',
      subtask_comment: entry.subtask_comment || '',
      hours: entry.hours != null ? String(entry.hours) : '',
      date: entry.date || '',
    });
    setEditDialogProjects(
      customerId
        ? allProjects.filter(p => p.customer_id === parseInt(customerId))
        : allProjects
    );
    if (projectId) {
      await fetchTasksForDialog({ project_id: projectId });
    } else if (customerId) {
      await fetchTasksForDialog({ customer_id: customerId });
    } else {
      setEditDialogStandardTasks([]);
      setEditDialogTimetrackerTasks([]);
    }
  };

  const closeEditDialog = () => {
    setEditDialogEntry(null);
    setEditDialogData({
      customer_id: '', project_id: '', task_id: '',
      subtask_comment: '', hours: '', date: '',
    });
    setEditDialogProjects([]);
    setEditDialogStandardTasks([]);
    setEditDialogTimetrackerTasks([]);
  };

  const handleDialogCustomerChange = async (customerId) => {
    setEditDialogData(prev => ({ ...prev, customer_id: customerId, project_id: '', task_id: '' }));
    setEditDialogProjects(
      customerId
        ? allProjects.filter(p => p.customer_id === parseInt(customerId))
        : []
    );
    if (customerId) {
      await fetchTasksForDialog({ customer_id: customerId });
    } else {
      setEditDialogStandardTasks([]);
      setEditDialogTimetrackerTasks([]);
    }
  };

  const handleDialogProjectChange = async (projectId) => {
    setEditDialogData(prev => ({ ...prev, project_id: projectId, task_id: '' }));
    if (projectId) {
      await fetchTasksForDialog({ project_id: projectId });
    } else if (editDialogData.customer_id) {
      await fetchTasksForDialog({ customer_id: editDialogData.customer_id });
    }
  };

  const handleEditDialogSave = async () => {
    const { customer_id, project_id, task_id, subtask_comment, hours, date } = editDialogData;
    if (!customer_id) {
      setError('Bitte einen Kunden auswählen');
      return;
    }
    if (!subtask_comment.trim()) {
      setError('Bitte einen Kommentar eingeben');
      return;
    }
    const hoursNum = parseFloat(hours);
    if (isNaN(hoursNum) || hoursNum <= 0) {
      setError('Bitte eine gültige Stundenanzahl eingeben');
      return;
    }
    if (!date) {
      setError('Bitte ein Datum auswählen');
      return;
    }

    setEditDialogSaving(true);
    setError('');
    try {
      const res = await timeEntriesAPI.update(editDialogEntry.id, {
        customer_id: toId(customer_id),
        project_id: toId(project_id),
        task_id: toId(task_id),
        subtask_comment,
        hours: hoursNum,
        date,
      });
      setTimeEntries(prev =>
        prev.map(e => (e.id === editDialogEntry.id ? res.data : e))
      );
      setSuccess('Zeiteintrag erfolgreich aktualisiert');
      closeEditDialog();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Aktualisieren des Zeiteintrags');
    } finally {
      setEditDialogSaving(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        customer_id: toId(formData.customer_id),
        project_id: toId(formData.project_id),
        task_id: toId(formData.task_id),
        hours: parseFloat(formData.hours),
        user_id: user.id,
      };

      if (editingId) {
        await timeEntriesAPI.update(editingId, data);
        setSuccess('Zeiteintrag erfolgreich aktualisiert');
      } else {
        await timeEntriesAPI.create(data);
        setSuccess('Zeiteintrag erfolgreich erstellt');
      }

      resetForm();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Zeiteintrags');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Zeiteintrag wirklich löschen?')) {
      return;
    }

    try {
      await timeEntriesAPI.delete(id);
      setSuccess('Zeiteintrag erfolgreich gelöscht');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen des Zeiteintrags');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      project_id: '',
      task_id: '',
      subtask_comment: '',
      hours: '',
      date: localISODate(),
    });
    setEditingId(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString + 'T00:00:00');
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Zuletzt selbst bebuchte Kunden zuerst (die Einträge kommen nach Datum absteigend)
  const sortedCustomers = sortCustomersByRecent(customers, recentIds(timeEntries, user.id));

  // Ein Auswahlfeld für beide Task-Arten; leer = ohne Task („Allgemeine Leistungen")
  const renderTaskOptions = (standard, timetracker) => [
    <MenuItem key="none" value="">Ohne Task (Allgemeine Leistungen)</MenuItem>,
    standard.length > 0 && <ListSubheader key="h-standard">Tickets</ListSubheader>,
    ...standard.map(task => (
      <MenuItem key={task.id} value={String(task.id)}>
        {task.task_id ? `${task.task_id} · ${task.name}` : task.name}
      </MenuItem>
    )),
    timetracker.length > 0 && <ListSubheader key="h-timetracker">Zeiterfassungs-Tasks</ListSubheader>,
    ...timetracker.map(task => (
      <MenuItem key={task.id} value={String(task.id)}>{task.name}</MenuItem>
    )),
  ].filter(Boolean);

  const toId = (value) => (value === '' || value == null ? null : parseInt(value));

  const groupedEntries = getGroupedEntries();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Zeiterfassung
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Time Entry Form */}
      <Card sx={{ borderRadius: 3, mb: 3, background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#1e3a5f', mb: 3 }}>
            Zeiteintrag erfassen
          </Typography>
          <Box component="form" onSubmit={handleSubmit}>
            {/* Zeile 1: Kunde und Projekt */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  fullWidth
                  label="Kunde"
                  name="customer_id"
                  value={formData.customer_id}
                  onChange={handleChange}
                  required
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                >
                  <MenuItem value="">Kunde auswählen</MenuItem>
                  {sortedCustomers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ flex: 1 }}>
                <TextField
                  select
                  fullWidth
                  label="Projekt (optional)"
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleChange}
                  disabled={!formData.customer_id}
                  helperText={!formData.customer_id ? "Bitte zuerst Kunde auswählen" : "Optional"}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                >
                  <MenuItem value="">Kein Projekt</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>

            {/* Zeile 2: Task (optional) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                select
                fullWidth
                label="Task (optional)"
                name="task_id"
                value={formData.task_id ? String(formData.task_id) : ''}
                onChange={handleChange}
                disabled={!formData.customer_id}
                helperText={!formData.customer_id
                  ? 'Bitte zuerst Kunde auswählen'
                  : 'Mit Task erscheint die Zeit im Bericht unter dem Ticket, ohne unter „Allgemeine Leistungen"'}
                SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 400 } } } }}
              >
                {renderTaskOptions(standardTasks, timetrackerTasks)}
              </TextField>
            </Box>

            {/* Zeile 3: Subtask Kommentar (volle Breite) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Subtask Kommentar"
                name="subtask_comment"
                value={formData.subtask_comment}
                onChange={handleChange}
                multiline
                rows={4}
                required
              />
            </Box>

            {/* Zeile 4: Stunden, Datum, Button */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ width: '150px' }}>
                <TextField
                  fullWidth
                  label="Stunden"
                  name="hours"
                  type="number"
                  value={formData.hours}
                  onChange={handleChange}
                  inputProps={{ step: 0.25, min: 0.25 }}
                  required
                />
              </Box>

              <Box sx={{ width: '200px' }}>
                <TextField
                  fullWidth
                  label="Datum"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Box>

              <Box sx={{ flex: 1 }} />

              <Box sx={{ width: '250px' }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  startIcon={<AddIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                    color: 'white',
                    fontWeight: 600,
                    py: 1.5,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
                    },
                  }}
                >
                  {editingId ? 'Aktualisieren' : 'Zeiteintrag erstellen'}
                </Button>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Time Entries Grouped by Project */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Meine Zeiteinträge
          </Typography>

          {/* Filter */}
          <Box sx={{ mt: 3, mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
              Filter
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Kunde"
                  value={filterCustomer}
                  onChange={(e) => setFilterCustomer(e.target.value)}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                >
                  <MenuItem value="">Alle Kunden</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.company_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Projekt"
                  value={filterProject}
                  onChange={(e) => setFilterProject(e.target.value)}
                  disabled={!filterCustomer && customers.length > 0}
                  helperText={!filterCustomer && customers.length > 0 ? "Bitte zuerst Kunde auswählen" : ""}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: 300 }
                      }
                    }
                  }}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiSelect-select': {
                      minWidth: '200px'
                    }
                  }}
                >
                  <MenuItem value="">Alle Projekte</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ width: '180px' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Von Datum"
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'white' }}
                />
              </Box>
              <Box sx={{ width: '180px' }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bis Datum"
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ bgcolor: 'white' }}
                />
              </Box>
            </Box>
          </Box>

          {groupedEntries.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <TimeIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Keine Zeiteinträge vorhanden
              </Typography>
              <Typography variant="body2">
                Erstellen Sie Ihren ersten Zeiteintrag oder passen Sie die Filter an!
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 3 }}>
              {groupedEntries.map((group) => (
                <Box key={group.group_key} sx={{ mb: 2 }}>
                  {/* Group Header (Projekt oder Kunde) */}
                  <Box
                    onClick={() => toggleProject(group.group_key)}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: '#eeeeee',
                      },
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IconButton size="small">
                        {expandedProjects[group.group_key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Typography variant="h6" fontWeight={600} sx={{ color: '#1e3a5f' }}>
                        {group.group_label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#616161' }}>
                        ({group.entries.length} Einträge{group.group_key.startsWith('c-') ? ' · ohne Projekt' : ''})
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#e01e26' }}>
                      {group.total_hours.toFixed(2)} h
                    </Typography>
                  </Box>

                  {/* Group Entries */}
                  <Collapse in={expandedProjects[group.group_key]} timeout="auto">
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderTop: 'none',
                        borderRadius: '0 0 8px 8px',
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#fafafa' }}>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#616161' }}>Datum</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#616161' }}>Task</TableCell>
                            <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#616161' }}>Beschreibung</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#616161' }}>Stunden</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#616161', width: '120px' }}>Aktionen</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.entries.map((entry) => (
                            <TableRow
                              key={entry.id}
                              sx={{
                                '&:hover': {
                                  bgcolor: '#f5f5f5',
                                },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {formatDate(entry.date)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#424242' }}>
                                  {entry.task_name || <span style={{ color: '#9ca3af' }}>Allgemein</span>}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#616161' }}>
                                  {entry.subtask_comment}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {editingEntry === entry.id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                    <TextField
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      onKeyDown={(e) => handleKeyDown(e, entry.id)}
                                      type="number"
                                      inputProps={{ step: 0.25, min: 0, style: { textAlign: 'right' } }}
                                      size="small"
                                      autoFocus
                                      disabled={saving}
                                      sx={{ width: '80px' }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditSave(entry.id)}
                                      disabled={saving}
                                      sx={{ color: '#10b981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)' } }}
                                    >
                                      {saving ? <CircularProgress size={16} /> : '✓'}
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleEditCancel}
                                      disabled={saving}
                                      sx={{ color: '#ef4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.1)' } }}
                                    >
                                      ✕
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box
                                    onClick={() => handleEditClick(entry.id, entry.hours)}
                                    sx={{
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 0.5,
                                      minWidth: '70px',
                                      justifyContent: 'flex-end',
                                      '&:hover': { bgcolor: '#fff3f3' },
                                    }}
                                  >
                                    <Typography variant="body2" fontWeight={600} sx={{ color: '#e01e26' }}>
                                      {parseFloat(entry.hours || 0).toFixed(2)} h
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem', ml: 0.5 }}>
                                      ✎
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditDialog(entry)}
                                  title="Bearbeiten"
                                  sx={{
                                    color: '#1e3a5f',
                                    mr: 0.5,
                                    '&:hover': { bgcolor: 'rgba(30, 58, 95, 0.08)' },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(entry.id)}
                                  title="Löschen"
                                  sx={{
                                    color: '#e01e26',
                                    '&:hover': { bgcolor: 'rgba(224, 30, 38, 0.1)' },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Vollständiger Edit-Dialog */}
      <Dialog
        open={!!editDialogEntry}
        onClose={editDialogSaving ? undefined : closeEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>
          Zeiteintrag bearbeiten
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Kunde"
              value={editDialogData.customer_id}
              onChange={(e) => handleDialogCustomerChange(e.target.value)}
              required
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
            >
              <MenuItem value="">Kunde auswählen</MenuItem>
              {sortedCustomers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.company_name}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Projekt (optional)"
              value={editDialogData.project_id}
              onChange={(e) => handleDialogProjectChange(e.target.value)}
              disabled={!editDialogData.customer_id}
              helperText={!editDialogData.customer_id ? 'Bitte zuerst Kunde auswählen' : 'Optional'}
              SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}
            >
              <MenuItem value="">Kein Projekt</MenuItem>
              {editDialogProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TextField
            select
            fullWidth
            label="Task (optional)"
            value={editDialogData.task_id ? String(editDialogData.task_id) : ''}
            onChange={(e) => setEditDialogData(prev => ({ ...prev, task_id: e.target.value }))}
            disabled={!editDialogData.customer_id}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 400 } } } }}
            sx={{ mb: 2 }}
          >
            {renderTaskOptions(editDialogStandardTasks, editDialogTimetrackerTasks)}
          </TextField>

          <TextField
            fullWidth
            label="Subtask Kommentar"
            value={editDialogData.subtask_comment}
            onChange={(e) => setEditDialogData(prev => ({ ...prev, subtask_comment: e.target.value }))}
            multiline
            rows={4}
            required
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Stunden"
              type="number"
              value={editDialogData.hours}
              onChange={(e) => setEditDialogData(prev => ({ ...prev, hours: e.target.value }))}
              inputProps={{ step: 0.25, min: 0.25 }}
              required
              sx={{ width: 150 }}
            />
            <TextField
              label="Datum"
              type="date"
              value={editDialogData.date}
              onChange={(e) => setEditDialogData(prev => ({ ...prev, date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              required
              sx={{ width: 200 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeEditDialog} disabled={editDialogSaving}>
            Abbrechen
          </Button>
          <Button
            onClick={handleEditDialogSave}
            disabled={editDialogSaving}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            {editDialogSaving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimeTrackingPage;
